import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Grid, GridColumn, Notice, Separator, Text } from 'telia-front-react';
import FormErrorNotice from '../components/FormErrorNotice';
import { useStores } from '../stores/RootStore';
import { scrollUpToElement } from '../util/HtmlUtil';
import BranchSelection from './common/fields/BranchSelection';
import CountryCallingCode from '../forms/fields/CountryCallingCode';
import CustomerTypeSelector from './common/fields/CustomerTypeSelector';
import DateAndTimeSelection from '../forms/fields/DateAndTimeSelection';
import EmailField from '../forms/fields/EmailField';
import PhoneField from '../forms/fields/PhoneField';
import ReservationErrorNotice from './common/ReservationErrorNotice';
import ReservationShowPreviewButton from './ReservationShowPreviewButton';
import TechnicianInformationBox from './TechnicianInformationBox';
import DescriptionField from '../forms/fields/DescriptionField';

const ReservationForm = observer((): React.JSX.Element => {
  const {
    reservationStore: {
      reservationCreationState: { status, errors },
      hasReservationCreationFormSubmitted,
      selectTime,
      selectedBranch,
      timesLoaded,
      availableReservations,
      reservationCreation,
      requiredFieldsAreValid,
      changeCustomerType,
      branchHasTechChild,
      branchHasB2BChild,
      prefillReservationCreation,
      getBranchHelper,
      getCallingCodeHelper,
      getPhoneHelper,
      getEmailHelper,
      getDateAndTimeHelper
    },
    countryCallingCodeStore: { isForeignCodeSelected },
    ssoStore: { customerId, customerSegmentType }
  } = useStores();
  const { translate } = React.useContext(TranslationsContext);
  const showError = ['ERROR', 'INVALID_INPUT'].includes(status);
  const { isMobile, isMobileOrTablet } = useIsMobileHook();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (selectedBranch && timesLoaded && !availableReservations.length) {
      scrollUpToElement('no-available-times-error-notice', isMobileOrTablet);
    }
  }, [selectedBranch, timesLoaded, availableReservations.length]);

  React.useEffect(() => {
    if (customerId) {
      prefillReservationCreation();
    }
  }, [customerId]);

  React.useEffect(() => {
    changeCustomerType(customerSegmentType);
  }, [customerSegmentType]);

  return (
    <>
      <Text>
        <p>{translate('reservation.description')}</p>
        <Separator color="invisible" spacing="xsmall" />
        {selectedBranch && timesLoaded && !availableReservations.length && (
          <>
            <Notice id="no-available-times-error-notice" type="error" attributes={{ 'aria-live': 'assertive' }}>
              {translate('reservation.noneAvailable')}
            </Notice>
            <Separator color="invisible" spacing="xsmall" />
          </>
        )}
        {showError && <ReservationErrorNotice status={status} errors={errors} />}
        {hasReservationCreationFormSubmitted && !requiredFieldsAreValid && (
          <FormErrorNotice
            erroredFields={[
              getBranchHelper(translate),
              getCallingCodeHelper(translate),
              getPhoneHelper(translate),
              isForeignCodeSelected ? getEmailHelper(translate) : undefined,
              getDateAndTimeHelper(translate)
            ]}
          />
        )}
        <BranchSelection />
        {branchHasB2BChild && <CustomerTypeSelector customerType={reservationCreation.subjectContext} onChange={changeCustomerType} />}
        {branchHasTechChild && <TechnicianInformationBox />}
        {!!customerId && (
          <DescriptionField
            value={reservationCreation.description}
            onChange={(value: string) => runInAction(() => (reservationCreation.description = value))}
            label={translate('reservation.descriptionField')}
          />
        )}
        <Grid>
          <CountryCallingCode getHelper={() => getCallingCodeHelper(translate)} shouldValidate={hasReservationCreationFormSubmitted} />
          <GridColumn width={['xs-12', 'sm-6', 'md-7']}>
            <PhoneField
              value={reservationCreation.phone}
              onChange={(value: string) => runInAction(() => (reservationCreation.phone = value))}
              getHelper={() => getPhoneHelper(translate)}
              shouldValidate={hasReservationCreationFormSubmitted}
              isDisabled={isForeignCodeSelected}
            />
          </GridColumn>
        </Grid>
        {isForeignCodeSelected && (
          <>
            <Notice size="small">
              <p className="text-light text-small">{translate('reservation.email.notice')}</p>
            </Notice>
            <EmailField
              value={reservationCreation.email}
              onChange={(value) => runInAction(() => (reservationCreation.email = value))}
              getHelper={() => getEmailHelper(translate)}
              shouldValidate={hasReservationCreationFormSubmitted}
            />
          </>
        )}
        <DateAndTimeSelection
          value={reservationCreation.time}
          onChange={selectTime}
          availableTimes={availableReservations}
          getHelper={() => getDateAndTimeHelper(translate)}
          shouldValidate={hasReservationCreationFormSubmitted}
          isDisabled={!timesLoaded || (timesLoaded && !availableReservations.length)}
          label={translate(`reservation.selectTime${isMobile ? '.mobile' : ''}`)}
        />
        <ReservationShowPreviewButton />
      </Text>
    </>
  );
});

export default ReservationForm;
